import React, { useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, UserPlus, Trash2, Users, Mail, Star } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "./ConfirmDialog";

/**
 * ClientContactsCard — manage the authorized contacts on an onboarding.
 *
 * The primary client (onb.client_email) is locked (can't be removed). Each
 * additional contact gets:
 *   • a magic-link invite email
 *   • access to the same onboarding via /portal
 *
 * Endpoints:
 *   POST    /api/onboardings/{id}/users       — add + send invite
 *   DELETE  /api/onboardings/{id}/users/{email}
 *
 * Backend already manages authorized_emails; this is the missing UI surface.
 */
export default function ClientContactsCard({ onb, onChange }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);  // email string
  // When the API blocks the add because the email is on another company, we
  // hold the conflict details here and offer a "Link anyway" affirmative.
  const [conflict, setConflict] = useState(null);  // {message, linked_to[]}

  const primary = (onb.client_email || "").toLowerCase();
  const allContacts = [...new Set((onb.authorized_emails || []).map(e => e.toLowerCase()))];
  // The backend stores primary in authorized_emails too — show it first with a marker
  const ordered = [primary, ...allContacts.filter(e => e !== primary)];

  const add = async (e, force = false) => {
    e?.preventDefault?.();
    const target = email.trim().toLowerCase();
    if (!target) return;
    setAdding(true);
    try {
      await api.post(`/onboardings/${onb.id}/users`, { email: target, note: note.trim(), force_link: force });
      toast.success(`Invitation sent to ${target}`);
      setEmail(""); setNote(""); setConflict(null);
      onChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Detail can be a string (legacy) OR a structured dict from the new
      // one-client-one-company guard. We pattern-match on the structured form.
      if (err?.response?.status === 409 && typeof detail === "object" && detail.error === "email_linked_to_other_company") {
        setConflict({ message: detail.message, linked_to: detail.linked_to || [] });
      } else {
        toast.error(typeof detail === "string" ? detail : "Add contact failed");
      }
    } finally { setAdding(false); }
  };

  const doRemove = async () => {
    if (!confirmRemove) return;
    const target = confirmRemove;
    setRemovingEmail(target);
    try {
      await api.delete(`/onboardings/${onb.id}/users/${encodeURIComponent(target)}`);
      toast.success(`${target} removed from this onboarding`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally { setRemovingEmail(null); }
  };

  return (
    <Card className="bg-white border-slate-200 p-5 mb-6 shadow-sm" data-testid="client-contacts-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="font-semibold text-brand-primary flex items-center gap-2"><Users size={16}/>Client contacts</h3>
        <span className="text-xs text-slate-500">{ordered.length} {ordered.length === 1 ? "person" : "people"} can access this onboarding</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Add additional people on the client side — they'll receive a magic-link invite and can collaborate on the same onboarding (e.g., one person for IT, one for HR, one for ownership decisions).</p>

      <form onSubmit={add} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 mb-5">
        <div className="flex-1 space-y-1.5">
          <Label className="text-slate-800 text-sm">Email</Label>
          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@example.com" className="pl-9" disabled={adding} required data-testid="contact-email-input"/>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          <Label className="text-slate-800 text-sm">Optional note in the email</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Please answer the M365 questions." disabled={adding} data-testid="contact-note-input"/>
        </div>
        <Button type="submit" disabled={adding || !email.trim()} style={{ background: "#00C7FF", color: "#0F1644" }} className="font-semibold shrink-0" data-testid="contact-add-btn">
          {adding ? <Loader2 size={14} className="animate-spin"/> : <><UserPlus size={14} className="mr-1.5"/>Send invitation</>}
        </Button>
      </form>

      {conflict && (
        <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="contact-conflict-banner">
          <div className="font-medium mb-1">Already linked to another company</div>
          <p className="text-amber-800">{conflict.message}</p>
          {conflict.linked_to.length > 0 && (
            <ul className="mt-2 ml-4 list-disc text-amber-800 space-y-0.5">
              {conflict.linked_to.map(o => <li key={o.id}>{o.company_name || o.id}</li>)}
            </ul>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={(e) => add(e, true)}
              disabled={adding}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              data-testid="contact-link-anyway-btn"
            >
              {adding ? <Loader2 size={12} className="animate-spin"/> : "Link anyway (shared contact)"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConflict(null)}
              className="text-amber-800 hover:bg-amber-100"
              data-testid="contact-conflict-dismiss"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5" data-testid="contacts-list">
        {ordered.map(e => {
          const isPrimary = e === primary;
          return (
            <div key={e} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200" data-testid={`contact-row-${e}`}>
              <div className="min-w-0 flex items-center gap-2 truncate">
                {isPrimary ? <Star size={12} className="text-amber-500 fill-amber-400 shrink-0" data-testid="primary-marker"/> : <Mail size={12} className="text-slate-500 shrink-0"/>}
                <span className="text-sm text-slate-900 truncate">{e}</span>
                {isPrimary && <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">primary</span>}
              </div>
              {!isPrimary && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRemove(e)}
                  disabled={removingEmail === e}
                  className="text-rose-600 hover:bg-rose-50 shrink-0"
                  data-testid={`remove-contact-${e}`}
                  title="Remove this contact"
                >
                  {removingEmail === e ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(v) => !v && setConfirmRemove(null)}
        title={confirmRemove ? `Remove ${confirmRemove}?` : ""}
        description={"They'll lose access to this onboarding on next magic-link sign-in. The primary client is unaffected."}
        confirmLabel="Remove contact"
        cancelLabel="Keep them"
        destructive
        testId="remove-contact-confirm"
        onConfirm={doRemove}
      />
    </Card>
  );
}
